import { UpdateIcon } from '@radix-ui/react-icons';
import { useCallback, useContext, useEffect, useState, } from 'react';
import { useForm } from 'react-hook-form';
import { AuthContext } from '../../context/AuthContext';
import api from '../../lib/api';
import { Button } from '../ui/button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../ui/dialog";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '../ui/form';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { toast } from '../ui/use-toast';

const ChangeBunnyClassDialog = ({ bunny, reloadBunnySinglePage, bunnyId, dropdown, isDialogOpen, setIsDialogOpen, onlyMount }) => {
    const [isLoading, setIsLoading] = useState(false);
    const form = useForm();
    const { auth } = useContext(AuthContext);
    const [formData, setFormData] = useState({
        straightClassId: bunny?.class_straight || null,
        crookedClassId: bunny?.class_crooked || null,
        lengthClassId: bunny?.class_length || null,
        heightClassId: bunny?.class_height || null,
    });

    const handleChange = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const response = await api.post(`https://skhrf-api.cmdigital.se/v1/bunnies/update-classes`, {
                bunnyId: bunny.id,
                straightClassId: formData.straightClassId,
                crookedClassId: formData.crookedClassId,
                lengthClassId: formData.lengthClassId,
                heightClassId: formData.heightClassId,
            });

            toast({
                title: 'Klass ändrat.',
                description: `${bunny.full_name} klass blev ändrad.`,
            });
            reloadBunnySinglePage();
            setIsLoading(false);
            setIsDialogOpen(false);
        } catch (error) {
            toast({
                variant: 'destructive',
                title: 'Något gick fel.',
                description: error?.response?.data?.error,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const fetchBunny = useCallback(async () => {
        if (bunnyId && isDialogOpen) {
            setIsLoading(true);
            try {
                const response = await api.get(`https://skhrf-api.cmdigital.se/v1/bunnies/?bunnyId=${bunnyId}`);
                setFormData({
                    straightClassId: response.data.class_straight,
                    crookedClassId: response.data.class_crooked,
                    lengthClassId: response.data.class_length,
                    heightClassId: response.data.class_height,
                });
            } catch (error) {
                console.error('Error fetching bunny data:', error);
                if (error.response?.status === 401) {
                    setIsDialogOpen(false);
                }
            } finally {
                setIsLoading(false);
            }
        }
    }, [bunnyId, isDialogOpen]);

    useEffect(() => {
        fetchBunny();
    }, [fetchBunny]);


    const handleClassChange = (name, value) => {

        setFormData((prevData) => ({
            ...prevData,
            [name]: parseInt(value),
        }));
    };

    return (
        <Dialog open={isDialogOpen}
            onOpenChange={(state) => {
                setIsDialogOpen(state);
            }}
        >
            {onlyMount !== true && (
                dropdown ? (
                    <DropdownMenuItem onSelect={(event) => event.preventDefault()}>
                        <DialogTrigger className='flex items-center w-full'>
                            Ändra klasser
                        </DialogTrigger>
                    </DropdownMenuItem>
                ) : (
                    <DialogTrigger className='flex items-center w-full' asChild>
                        <Button variant='secondary'>
                            Ändra klasser
                        </Button>
                    </DialogTrigger>
                )
            )}
            <DialogContent className="flex flex-col gap-6">
                <DialogHeader className='text-left'>
                    <DialogTitle>
                        Ändra klasser
                    </DialogTitle>
                    <DialogDescription>
                        Uppdatera kaninens klasser.
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={handleChange} className="flex flex-col gap-6">
                        <div className='grid grid-cols-1'>
                            <div className='grid grid-cols-2 gap-6'>
                                <FormField
                                    control={form.control}
                                    name="straight"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Rak</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    value={formData.straightClassId}
                                                    onValueChange={(value) => handleClassChange('straightClassId', value)}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem
                                                            value={1}
                                                            id="1"
                                                            disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge}
                                                        />
                                                        <Label htmlFor="1">Mini</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem
                                                            value={2}
                                                            id="2"
                                                            disabled={(
                                                                (!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge) &&
                                                                bunny?.class_straight !== 2 &&
                                                                bunny?.class_straight !== 1
                                                            )}
                                                        />
                                                        <Label htmlFor="2">Lätt</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={3} id="3" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="3">Medelsvår</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={4} id="4" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="4">Svår</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={5} id="5" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="5">Elit</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={6} id="6" />
                                                        <Label htmlFor="6">Veteran</Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="crooked"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Krok</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    value={formData.crookedClassId}
                                                    onValueChange={(value) => handleClassChange('crookedClassId', value)}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem
                                                            value={7}
                                                            id="7"
                                                            disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge}
                                                        />
                                                        <Label htmlFor="7">Mini</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem
                                                            value={8}
                                                            id="8"
                                                            disabled={(
                                                                (!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge) &&
                                                                bunny?.class_crooked !== 8 &&
                                                                bunny?.class_crooked !== 7
                                                            )}
                                                        />
                                                        <Label htmlFor="8">Lätt</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={9} id="9" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="9">Medelsvår</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={10} id="10" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="10">Svår</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={11} id="11" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="11">Elit</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={12} id="12" />
                                                        <Label htmlFor="12">Veteran</Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="high"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Höjd</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    value={formData.heightClassId}
                                                    onValueChange={(value) => handleClassChange('heightClassId', value)}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={13} id="13" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="13">Ej elit</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={14} id="14" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="14">Elit</Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="length"
                                    render={({ field }) => (
                                        <FormItem className='space-y-0 grid gap-2'>
                                            <FormLabel>Längd</FormLabel>
                                            <FormControl>
                                                <RadioGroup
                                                    value={formData.lengthClassId}
                                                    onValueChange={(value) => handleClassChange('lengthClassId', value)}
                                                >
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={16} id="16" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="16">Ej elit</Label>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                        <RadioGroupItem value={17} id="17" disabled={!auth.user?.superadmin && !auth.user?.bunnyadmin && !auth.user?.judge} />
                                                        <Label htmlFor="17">Elit</Label>
                                                    </div>
                                                </RadioGroup>
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-3'>
                            <DialogClose asChild>
                                <Button type='button' variant='secondary'>Avbryt</Button>
                            </DialogClose>
                            <Button
                                onClick={handleChange}
                                disabled={isLoading}>
                                {(isLoading) ? <UpdateIcon className='loader-spinner' /> : <>Uppdatera</>}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
};

export default ChangeBunnyClassDialog;
